import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Grid, Menu } from '@mui/material';
import { useFormikContext } from 'formik';
import { MouseEvent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { isHeadTrainer } from 'src/models/user/utils';
import { useTasks } from 'src/pages/TimeTracking/useTasks';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { useTrainerSettings } from './useTrainerSettings';

const gridItemCommonSize = {
  xs: 12,
  sm: 6,
  md: 4,
};

export const TrainerPermissions = ({ namePrefix }: { namePrefix: string }) => {
  const { t: tSummary } = useTranslation();
  const { t } = useTranslation('translation', {
    keyPrefix: 'staff.settings',
  });

  const { session, user } = useSession();
  const { values, setFieldValue } = useFormikContext<any>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    additionalPermissions,
    locationPermissions,
    connectedAppsPermissions,
    fbmPermissions,
    permissions,
    cancelVisitPermissions,
    clientPackagePermissions,
    subTrainerPermissionOptions,
    clockPermissionOptions,
    inventoryPermissionOptions,
    clientManagementTabs,
    dashboardBoxes,
    dashboardNotifications,
    emailOptions,
    adminEmails,
    allReports,
    kpiBlueprints,
    chartBlueprints,
    kpiBlueprintsLoading,
    chartBlueprintsLoading,
  } = useTrainerSettings();

  const { tasks } = useTasks();

  const vs = useMemo(() => {
    const keys = namePrefix.split('.');
    return keys.reduce((acc, key) => acc[key], values);
  }, [values, namePrefix]);

  const options = useMemo(() => {
    return additionalPermissions.map((permission) => ({
      value: permission.id as unknown as string,
      label: permission.name,
    }));
  }, [additionalPermissions]);

  const clientManagementValues = useMemo(() => {
    const disabledIds = new Set(vs.disabledTabs);
    return (clientManagementTabs || [])
      .filter((tab) => !disabledIds.has(tab.id))
      .map((tab) => tab.id);
  }, [clientManagementTabs, vs.disabledTabs]);

  const dashboardBoxesValues = useMemo(() => {
    const disabledIds = new Set(vs.disabledDashboardBoxes);
    return (dashboardBoxes || [])
      .filter((box) => !disabledIds.has(box.value))
      .map((box) => box.value);
  }, [dashboardBoxes, vs.disabledDashboardBoxes]);

  const dashboardNotificationsValues = useMemo(() => {
    const disabledIds = new Set(vs.disabledDashboardNotifications);
    return (dashboardNotifications || [])
      .filter((notification) => !disabledIds.has(notification.value))
      .map((notification) => notification.value);
  }, [dashboardNotifications, vs.disabledDashboardNotifications]);

  const emailValues = useMemo(() => {
    const disabledEmails = new Set(vs.disabledEmails);

    return (emailOptions || [])
      .filter((email) => !disabledEmails.has(email.value))
      .map((email) => email.value);
  }, [emailOptions, vs.disabledEmails]);

  const hasNotifications = useMemo(() => {
    return (
      !(vs.disabledDashboardBoxes || []).includes('Notifications') &&
      vs.clientsRole !== 2
    );
  }, [vs.disabledDashboardBoxes, vs.clientsRole]);

  const kpiValues = useMemo(() => {
    const disabledIds = new Set(vs.disabledDashboardKpis || []);
    return (kpiBlueprints || [])
      .filter((kpi) => !disabledIds.has(kpi.id))
      .map((kpi) => kpi.id);
  }, [kpiBlueprints, vs.disabledDashboardKpis]);

  const chartValues = useMemo(() => {
    const disabledIds = new Set(vs.disabledDashboardCharts || []);
    return (chartBlueprints || [])
      .filter((chart) => !disabledIds.has(chart.id))
      .map((chart) => chart.id);
  }, [chartBlueprints, vs.disabledDashboardCharts]);

  const allowedTasksValues = useMemo(() => {
    const disabledTaskIds = new Set(vs.disabledTasks || []);

    return (tasks || [])
      .filter((task) => !disabledTaskIds.has(task.id.toString()))
      .map((task) => task.id);
  }, [tasks, vs.disabledTasks]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={options}
            label={t('workoutPlans')}
            name={`${namePrefix}.workoutPlansRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={options}
            label={t('routines')}
            name={`${namePrefix}.routinesRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={options}
            label={t('exercises')}
            name={`${namePrefix}.exercisesRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={locationPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('clients')}
            name={`${namePrefix}.clientsRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={connectedAppsPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('connectedApps')}
            name={`${namePrefix}.connectedAppsRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={additionalPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('assignments')}
            name={`${namePrefix}.assessmentsRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={locationPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('messages')}
            name={`${namePrefix}.messagesRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={additionalPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('resources')}
            name={`${namePrefix}.resourcesRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={additionalPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('videos')}
            name={`${namePrefix}.videosRole`}
            size="medium"
          />
        </Grid>
        {session.enableFbm && (
          <>
            <Grid size={gridItemCommonSize}>
              <FormSelect
                options={fbmPermissions.map((permission) => ({
                  value: permission.id as unknown as string,
                  label: permission.name,
                }))}
                label={t('scheduling')}
                name={`${namePrefix}.schedulingRole`}
                size="medium"
              />
            </Grid>
            <Grid size={gridItemCommonSize}>
              <FormSelect
                options={permissions.map((permission) => ({
                  value: permission.id as unknown as string,
                  label: permission.name,
                }))}
                label={t('availabilitySchedules')}
                name={`${namePrefix}.schedulesRole`}
                size="medium"
              />
            </Grid>
            <Grid size={gridItemCommonSize}>
              <FormSelect
                options={cancelVisitPermissions.map((permission) => ({
                  value: permission.id as unknown as string,
                  label: permission.name,
                }))}
                label={t('cancelVisits')}
                name={`${namePrefix}.cancelVisitsRole`}
                size="medium"
              />
            </Grid>
            <Grid size={gridItemCommonSize}>
              <FormSelect
                options={clientPackagePermissions.map((permission) => ({
                  value: permission.id as unknown as string,
                  label: permission.name,
                }))}
                label={t('clientMemberships')}
                name={`${namePrefix}.clientPackagesRole`}
                size="medium"
              />
            </Grid>
          </>
        )}
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={additionalPermissions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('groups')}
            name={`${namePrefix}.groupsRole`}
            size="medium"
          />
          <HtmlTypography component="i">
            {t('groupWarning', {
              subTrainer: tSummary('trainer.other').toLowerCase(),
              group: tSummary('group.other').toLowerCase(),
            })}
          </HtmlTypography>
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={subTrainerPermissionOptions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('manageStaff')}
            name={`${namePrefix}.subtrainerRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={clockPermissionOptions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('manageTimecard')}
            name={`${namePrefix}.clockRole`}
            size="medium"
          />
        </Grid>
        <Grid size={gridItemCommonSize}>
          <FormSelect
            options={inventoryPermissionOptions.map((permission) => ({
              value: permission.id as unknown as string,
              label: permission.name,
            }))}
            label={t('manageInventory')}
            name={`${namePrefix}.inventoryRole`}
            size="medium"
          />
        </Grid>
      </Grid>

      <Stack direction="column" spacing={3} mt={4}>
        {[0, 1, 3].includes(vs.clientsRole) && (
          <>
            <BaseSelect
              options={clientManagementTabs.map((permission) => ({
                id: permission.id,
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              multiple
              label={
                <HtmlTypography component="span">
                  {t('clientManageTabs', {
                    subTrainer: tSummary('trainer.one').toLowerCase(),
                  })}
                </HtmlTypography>
              }
              sx={{
                '& .MuiInputLabel-root': {
                  whiteSpace: 'wrap',
                },
              }}
              value={clientManagementValues}
              onChange={(enabledTabs) => {
                const enabledSet = new Set(enabledTabs || []);
                const disabledTabs = clientManagementTabs
                  .filter((tab) => !enabledSet.has(tab.id))
                  .map((tab) => tab.id);

                setFieldValue(`${namePrefix}.disabledTabs`, disabledTabs);
              }}
            />
          </>
        )}
        {hasFeatureFlag(session, FeatureFlag.NewDashboard) ? (
          <>
            <Stack spacing={1}>
              <Box>
                <HtmlTypography component="span">
                  {t('dashboardKpis')}
                </HtmlTypography>
                <RouterLink to="/dashboard" target="_blank">
                  {t('dashboard')}
                </RouterLink>
              </Box>
              <BaseSelect
                options={(kpiBlueprints || []).map((kpi) => ({
                  id: kpi.id,
                  value: kpi.id,
                  label: kpi.name,
                }))}
                multiple
                value={kpiValues}
                disabled={kpiBlueprintsLoading}
                onChange={(enabledKpis) => {
                  const enabledSet = new Set(enabledKpis || []);
                  const disabledKpis = (kpiBlueprints || [])
                    .filter((kpi) => !enabledSet.has(kpi.id))
                    .map((kpi) => kpi.id);

                  setFieldValue(
                    `${namePrefix}.disabledDashboardKpis`,
                    disabledKpis
                  );
                }}
              />
            </Stack>
            <Stack spacing={1}>
              <Box>
                <HtmlTypography component="span">
                  {t('dashboardCharts')}
                </HtmlTypography>
                <RouterLink to="/dashboard" target="_blank">
                  {t('dashboard')}
                </RouterLink>
              </Box>
              <BaseSelect
                options={(chartBlueprints || []).map((chart) => ({
                  id: chart.id,
                  value: chart.id,
                  label: chart.name,
                }))}
                multiple
                value={chartValues}
                disabled={chartBlueprintsLoading}
                onChange={(enabledCharts) => {
                  const enabledSet = new Set(enabledCharts || []);
                  const disabledCharts = (chartBlueprints || [])
                    .filter((chart) => !enabledSet.has(chart.id))
                    .map((chart) => chart.id);

                  setFieldValue(
                    `${namePrefix}.disabledDashboardCharts`,
                    disabledCharts
                  );
                }}
              />
            </Stack>
          </>
        ) : (
          <>
            <Stack spacing={1}>
              <Box>
                <Box>
                  <HtmlTypography component="span">
                    {t('dashboardBoxes')}
                  </HtmlTypography>
                  <Link href={externalLinks.trainerDashboard} target="_blank">
                    {t('dashboard')}
                  </Link>
                </Box>
                <Link component="button" onClick={handleClick}>
                  {t('seeExceptions')}
                </Link>
              </Box>
              <BaseSelect
                options={dashboardBoxes.map((permission) => ({
                  id: permission.value,
                  value: permission.value,
                  label: permission.name,
                }))}
                multiple
                value={dashboardBoxesValues}
                onChange={(enabledBoxes) => {
                  const enabledSet = new Set(enabledBoxes || []);
                  const disabledBoxes = dashboardBoxes
                    .filter((box) => !enabledSet.has(box.value))
                    .map((box) => box.value);

                  setFieldValue(
                    `${namePrefix}.disabledDashboardBoxes`,
                    disabledBoxes
                  );
                }}
              />
            </Stack>
            {hasNotifications && (
              <Stack spacing={1}>
                <Box>
                  <HtmlTypography component="span">
                    {t('dashboardNotifications')}
                  </HtmlTypography>
                  <Link href={externalLinks.trainerDashboard} target="_blank">
                    {t('dashboard')}
                  </Link>
                </Box>
                <BaseSelect
                  options={dashboardNotifications.map((permission) => ({
                    id: permission.value,
                    value: permission.value,
                    label: permission.name,
                  }))}
                  multiple
                  value={dashboardNotificationsValues}
                  onChange={(enabledNotifications) => {
                    const enabledSet = new Set(enabledNotifications || []);
                    const disabledNotifications = dashboardNotifications
                      .filter(
                        (notification) => !enabledSet.has(notification.value)
                      )
                      .map((notification) => notification.value);

                    setFieldValue(
                      `${namePrefix}.disabledDashboardNotifications`,
                      disabledNotifications
                    );
                  }}
                />
              </Stack>
            )}
          </>
        )}

        {emailOptions.length > 0 && (
          <BaseSelect
            options={emailOptions.map((email) => ({
              id: email.value,
              value: email.value,
              label: email.name,
            }))}
            multiple
            label={
              <Box>
                <HtmlTypography component="span">
                  {t('emails', {
                    subTrainer: tSummary('trainer.one').toLowerCase(),
                  })}
                </HtmlTypography>
              </Box>
            }
            sx={{
              '& .MuiInputLabel-root': {
                whiteSpace: 'wrap',
              },
            }}
            value={emailValues}
            onChange={(enabledEmails) => {
              const enabledSet = new Set(enabledEmails || []);
              const disabledEmails = emailOptions
                .filter((email) => !enabledSet.has(email.value))
                .map((email) => email.value);

              setFieldValue(`${namePrefix}.disabledEmails`, disabledEmails);
            }}
          />
        )}

        {hasNotifications && (
          <Stack spacing={1}>
            <Box>
              <HtmlTypography component="span">
                {t('adminEmails', {
                  subTrainer: tSummary('trainer.one').toLowerCase(),
                })}
              </HtmlTypography>
              {isHeadTrainer(user) &&
                t('forDescription', {
                  type: t('email').toLowerCase(),
                })}
              <Link href={externalLinks.notifications} target="_blank">
                {t('adminEmailsHelperTextLink')}
              </Link>
            </Box>
            <BaseSelect
              options={adminEmails.map((email) => ({
                id: email.value,
                value: email.value,
                label: email.name,
              }))}
              multiple
              value={vs.headTrainerEmails}
              onChange={(emails) => {
                setFieldValue(`${namePrefix}.headTrainerEmails`, emails || []);
              }}
            />
          </Stack>
        )}

        {hasFeatureFlag(session, FeatureFlag.TimeTracking) && (
          <Stack spacing={1}>
            <BaseSelect
              options={tasks.map((task) => ({
                id: task.id,
                value: task.id as unknown as string,
                label: task.name,
              }))}
              multiple
              label={
                <Box>
                  <HtmlTypography component="span">
                    {t('tasks', {
                      subTrainer: tSummary('trainer.one').toLowerCase(),
                    })}
                  </HtmlTypography>
                </Box>
              }
              sx={{
                '& .MuiInputLabel-root': {
                  whiteSpace: 'wrap',
                },
              }}
              value={allowedTasksValues}
              onChange={(enabledTasks) => {
                const enabledSet = new Set(enabledTasks || []);
                const disabledTasks = tasks
                  .filter((task) => !enabledSet.has(task.id))
                  .map((task) => task.id.toString());

                setFieldValue(`${namePrefix}.disabledTasks`, disabledTasks);
              }}
            />
          </Stack>
        )}

        <Stack spacing={1}>
          <Box>
            <HtmlTypography component="span">
              {t('reportsLabel', {
                subTrainer: tSummary('trainer.one').toLowerCase(),
              })}
            </HtmlTypography>
            {isHeadTrainer(user) &&
              t('forDescription', {
                type: t('report').toLowerCase(),
              })}
            <Link href="/ex4/reports/" target="_blank">
              {t('reportsPage')}
            </Link>
          </Box>
          <BaseSelect
            options={allReports.map((report) => ({
              id: report.id,
              label: report.name,
            }))}
            multiple
            value={vs.reportsRole}
            onChange={(reports) => {
              setFieldValue(`${namePrefix}.reportsRole`, reports || []);
            }}
          />
          <Link
            component="button"
            onClick={() => {
              setFieldValue(
                `${namePrefix}.reportsRole`,
                allReports.map((report) => report.id)
              );
            }}
            sx={{
              width: 'fit-content',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Icon icon={icon({ name: 'plus', style: 'regular' })} />
            {t('addAllReports')}
          </Link>
        </Stack>
      </Stack>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {[t('seeExceptionsItem1'), t('seeExceptionsItem2')].map((item) => (
          <ListItem
            key={item}
            onClick={() => {
              handleClose();
            }}
            sx={{
              maxWidth: '400px',
            }}
            primary={
              <HtmlTypography variant="body2" component="span">
                {item}
              </HtmlTypography>
            }
          />
        ))}
      </Menu>
    </>
  );
};

export default memo(TrainerPermissions);
